import { createAsyncThunk } from "@reduxjs/toolkit";
import { iStateMessage } from "@/customTypes/GenericReduxTypes";
import { iGenericResponse } from "@/customTypes/CommonServiceTypes";
import { curryGetThunkName } from "@/projectUtils/ReduxUtil";
import { httpStatusCodes } from "@/customTypes/NetworkTypes";
import { commissionservice } from "@/services/api/cresivoxServer";
import { iCommissionDTO, REDUCER_NAME } from "./Types";

const curriedGetThunkName = curryGetThunkName(REDUCER_NAME);

// Helper function to extract error message from various response structures
const extractErrorMessage = (error: any, defaultMessage: string): string => {
  // Try different common error message locations
  if (typeof error === 'string') return error;
  
  return error?.message || 
         error?.data?.message || 
         error?.error?.message ||
         error?.data?.error?.message ||
         defaultMessage;
};

// ===================== GET COMMISSION LIST =====================
export const getCommissionList = createAsyncThunk<
  iGenericResponse<any[]> | null,
  void,
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("commissionList"),
  // @ts-expect-error: Type mismatch between LoginSchema and userLogin expected types
  async (_, { rejectWithValue }) => {
    try {
      const response = await commissionservice.getCommissionList();

      if (response) {
        const { httpStatusCode, data, message } = response;

        switch (httpStatusCode) {
          case httpStatusCodes.SUCCESS_OK: {
            if (data?.data) {
              return {
                httpStatusCode,
                data: data.data,
                message,
              };
            }
            return rejectWithValue({ httpStatusCode, message });
          }

          default:
            return rejectWithValue({ httpStatusCode, message });
        }
      }

      return rejectWithValue({ message: "No response received" });
    } catch (error) {
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);

// ===================== ADD COMMISSION =====================
export const addCommission = createAsyncThunk<
  iGenericResponse<any> | null,
  iCommissionDTO,
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("addCommission"),
  // @ts-expect-error: Type mismatch between LoginSchema and userLogin expected types
  async (payload, { rejectWithValue }) => {
    try {
      const response = await commissionservice.addCommission(payload);

      if (response) {
        const { httpStatusCode, data, message } = response;

        switch (httpStatusCode) {
          case httpStatusCodes.SUCCESS_OK:
          case 201:
            if (data !== undefined && data !== null) {
              return {
                httpStatusCode,
                data,
                message: extractErrorMessage(data, message || "Commission added successfully"),
              };
            }
            return rejectWithValue({
              httpStatusCode,
              message: "No data in response",
            });

          case 400: 
          //@ts-ignore
          case 409: 
            // Extract error message from nested structure
            const errorMsg = extractErrorMessage(data, "Another theme Category with this title already exists");
            return rejectWithValue({
              httpStatusCode,
              message: errorMsg,
            });

          default:
            const defaultErrorMsg = extractErrorMessage(data, "Failed to add commission");
            return rejectWithValue({ 
              httpStatusCode, 
              message: defaultErrorMsg
            });
        }
      }

      return rejectWithValue({ message: "No response received" });
    } catch (error: any) {
      
      // Extract error message from the caught error
      const errorMessage = extractErrorMessage(
        error?.response?.data || error, 
        "Something went wrong"
      );
      
      
      return rejectWithValue({ 
        message: errorMessage,
        httpStatusCode: error?.response?.status 
      });
    }
  }
);

// ===================== UPDATE COMMISSION =====================
export const updateCommission = createAsyncThunk<
  iGenericResponse<any> | null,
  iCommissionDTO,
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("updateCommission"),
  // @ts-expect-error: Type mismatch between LoginSchema and userLogin expected types
  async (payload, { rejectWithValue }) => {
    try {
      const response = await commissionservice.updateCommission(payload);

      if (response) {
        const { httpStatusCode, data, message } = response;

        switch (httpStatusCode) {
          case httpStatusCodes.SUCCESS_OK:
          case 201:
            if (data !== undefined && data !== null) {
              return {
                httpStatusCode,
                data,
                message: extractErrorMessage(data, message || "Commission updated successfully"),
              };
            }
            return rejectWithValue({
              httpStatusCode,
              message: "No data in response",
            });

          case 400:
            const badRequestMsg = extractErrorMessage(data, "Invalid data provided");
            return rejectWithValue({
              httpStatusCode,
              message: badRequestMsg,
            });

          case 404:
            return rejectWithValue({
              httpStatusCode,
              message: "Commission not found",
            });

          default:
            const defaultErrorMsg = extractErrorMessage(data, "Failed to update commission");
            return rejectWithValue({ 
              httpStatusCode, 
              message: defaultErrorMsg
            });
        }
      }

      return rejectWithValue({ message: "No response received" });
    } catch (error: any) {
      
      const errorMessage = extractErrorMessage(
        error?.response?.data || error, 
        "Something went wrong"
      );
      
      return rejectWithValue({ 
        message: errorMessage,
        httpStatusCode: error?.response?.status 
      });
    }
  }
);